import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  Checkbox,
  FormControlLabel,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  ON_SITE_STEP_META,
  OnSiteChecklistItem,
  OnSiteChecklistSubtask,
  parseOnSiteSubtasks,
  serializeOnSiteSubtasks
} from 'src/interfaces/OnSiteChecklistTypes';
import { ON_SITE_IMAGES } from 'src/assets/installation-checklist/sodistore-home';

interface Props {
  item: OnSiteChecklistItem;
  disabled: boolean;
  onUpdate: (
    id: number,
    patch: Partial<{ checked: boolean; comments: string; subtasks: string }>
  ) => Promise<boolean>;
  onImageClick: (filename: string, alt: string) => void;
  // Phones (<md): render as a stacked Card instead of a wide 3-column table row
  // (which forces page-level horizontal overflow). Desktop keeps the TableRow.
  isMobile?: boolean;
}

function OnSiteChecklistStepRow({
  item,
  disabled,
  onUpdate,
  onImageClick,
  isMobile
}: Props) {
  const intl = useIntl();
  const [comments, setComments] = useState(item.comments ?? '');
  const [subtasks, setSubtasks] = useState<OnSiteChecklistSubtask[]>(() =>
    parseOnSiteSubtasks(item.subtasks)
  );

  useEffect(() => {
    setComments(item.comments ?? '');
  }, [item.comments]);

  useEffect(() => {
    setSubtasks(parseOnSiteSubtasks(item.subtasks));
  }, [item.subtasks]);

  const meta = useMemo(
    () => ON_SITE_STEP_META.find((m) => m.number === item.stepNumber),
    [item.stepNumber]
  );

  const taskCount = subtasks.filter((s) => s.kind === 'task').length;
  const checkedCount = subtasks.filter((s) => s.kind === 'task' && s.checked).length;
  const allDone = taskCount > 0 && checkedCount === taskCount;

  // Auto-mirror item.checked from all-substasks-done.
  useEffect(() => {
    if (disabled) return;
    if (taskCount === 0) return;
    if (allDone && !item.checked) {
      onUpdate(item.id, { checked: true });
    } else if (!allDone && item.checked) {
      onUpdate(item.id, { checked: false });
    }
  }, [allDone, taskCount, item.checked, disabled, item.id, onUpdate]);

  const handleSubtaskToggle = async (index: number) => {
    if (disabled) return;
    const updated = subtasks.map((s, i) => {
      if (i !== index) return s;
      if (s.kind !== 'task') return s;
      return { ...s, checked: !s.checked };
    });
    setSubtasks(updated);
    await onUpdate(item.id, { subtasks: serializeOnSiteSubtasks(updated) });
  };

  const handleCommentsBlur = async () => {
    if (disabled) return;
    if (comments !== (item.comments ?? '')) {
      await onUpdate(item.id, { comments });
    }
  };

  const stepTitle = meta
    ? intl.formatMessage({ id: meta.titleKey, defaultMessage: item.stepKey })
    : item.stepKey;

  // Phones: stack the row's three cells vertically inside one Card so the tab
  // fits the viewport (no left cut-off). Desktop keeps the table row below.
  if (isMobile) {
    return (
      <Card variant="outlined" sx={{ p: 2, mb: 1.5 }}>
        <Stack spacing={1}>
          <Stack direction="row" alignItems="baseline" spacing={1}>
            <Typography variant="h5" fontWeight={700}>
              {/* Display offset: the "Installation details" section is step 1, so
                  the physical steps (stored 1–8) render as 2–9. */}
              {item.stepNumber + 1}.
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ flex: 1 }}>
              {stepTitle}
            </Typography>
            {taskCount > 0 && (
              <Typography
                variant="body2"
                color={allDone ? 'success.main' : 'text.secondary'}
              >
                ({checkedCount}/{taskCount})
              </Typography>
            )}
          </Stack>

          <Box>
            {subtasks.map((s, i) => {
              if (s.kind === 'heading') {
                return (
                  <Typography
                    key={`h-${i}`}
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mt: 1.5, mb: 0.5 }}
                  >
                    <FormattedMessage id={s.heading} defaultMessage={s.heading} />
                  </Typography>
                );
              }
              return (
                <FormControlLabel
                  key={`t-${i}`}
                  control={
                    <Checkbox
                      size="small"
                      checked={s.checked}
                      disabled={disabled}
                      onChange={() => handleSubtaskToggle(i)}
                    />
                  }
                  label={
                    <Typography variant="body2" component="span">
                      <FormattedMessage id={s.text} defaultMessage={s.text} />
                    </Typography>
                  }
                  sx={{ display: 'flex', alignItems: 'flex-start', ml: 0 }}
                />
              );
            })}
          </Box>

          {(meta?.warnings ?? []).map((w) => (
            <Alert
              key={w.key}
              severity={w.severity}
              variant="outlined"
              sx={{ mt: 1 }}
            >
              <FormattedMessage id={w.key} defaultMessage="" />
            </Alert>
          ))}

          <TextField
            size="small"
            multiline
            minRows={1}
            maxRows={6}
            value={comments}
            disabled={disabled}
            onChange={(e) => setComments(e.target.value)}
            onBlur={handleCommentsBlur}
            fullWidth
            placeholder={intl.formatMessage({
              id: 'onSiteCommentsPlaceholder',
              defaultMessage: 'Notizen, Beobachtungen…'
            })}
            sx={{ mt: 1 }}
          />

          {(meta?.images ?? []).length > 0 && (
            <Stack spacing={1} sx={{ mt: 1 }}>
              {(meta?.images ?? []).map((filename) => {
                const src = ON_SITE_IMAGES[filename];
                if (!src) return null;
                return (
                  <Box
                    key={filename}
                    component="img"
                    src={src}
                    alt={stepTitle}
                    onClick={() => onImageClick(filename, stepTitle)}
                    sx={{
                      width: '100%',
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: 1,
                      cursor: 'zoom-in',
                      border: '1px solid #e0e0e0',
                      transition: 'transform 0.1s',
                      '&:hover': { transform: 'scale(1.02)' }
                    }}
                  />
                );
              })}
            </Stack>
          )}
        </Stack>
      </Card>
    );
  }

  return (
    <TableRow hover sx={{ verticalAlign: 'top' }}>
      <TableCell sx={{ width: 48, verticalAlign: 'top', pt: 2 }}>
        <Typography variant="body1" fontWeight={700}>
          {/* Display offset: the "Installation details" section is step 1, so the
              physical steps (stored 1–8) render as 2–9. */}
          {item.stepNumber + 1}
        </Typography>
      </TableCell>

      <TableCell sx={{ verticalAlign: 'top', minWidth: 360, pt: 2 }}>
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h5" fontWeight={700}>
              {stepTitle}
            </Typography>
            {taskCount > 0 && (
              <Typography variant="body2" color={allDone ? 'success.main' : 'text.secondary'}>
                ({checkedCount}/{taskCount})
              </Typography>
            )}
          </Stack>

          <Box>
            {subtasks.map((s, i) => {
              if (s.kind === 'heading') {
                return (
                  <Typography
                    key={`h-${i}`}
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mt: 1.5, mb: 0.5 }}
                  >
                    <FormattedMessage id={s.heading} defaultMessage={s.heading} />
                  </Typography>
                );
              }
              return (
                <FormControlLabel
                  key={`t-${i}`}
                  control={
                    <Checkbox
                      size="small"
                      checked={s.checked}
                      disabled={disabled}
                      onChange={() => handleSubtaskToggle(i)}
                    />
                  }
                  label={
                    <Typography variant="body2" component="span">
                      <FormattedMessage id={s.text} defaultMessage={s.text} />
                    </Typography>
                  }
                  sx={{ display: 'flex', alignItems: 'flex-start', ml: 0 }}
                />
              );
            })}
          </Box>

          {(meta?.warnings ?? []).map((w) => (
            <Alert key={w.key} severity={w.severity} variant="outlined" sx={{ mt: 1 }}>
              <FormattedMessage id={w.key} defaultMessage="" />
            </Alert>
          ))}

          <TextField
            size="small"
            multiline
            minRows={1}
            maxRows={6}
            value={comments}
            disabled={disabled}
            onChange={(e) => setComments(e.target.value)}
            onBlur={handleCommentsBlur}
            fullWidth
            placeholder={intl.formatMessage({
              id: 'onSiteCommentsPlaceholder',
              defaultMessage: 'Notizen, Beobachtungen…'
            })}
            sx={{ mt: 1, maxWidth: 600 }}
          />
        </Stack>
      </TableCell>

      <TableCell sx={{ verticalAlign: 'top', width: 260, pt: 2 }}>
        <Stack spacing={1}>
          {(meta?.images ?? []).map((filename) => {
            const src = ON_SITE_IMAGES[filename];
            if (!src) return null;
            return (
              <Box
                key={filename}
                component="img"
                src={src}
                alt={stepTitle}
                onClick={() => onImageClick(filename, stepTitle)}
                sx={{
                  width: '100%',
                  maxWidth: 240,
                  height: 'auto',
                  borderRadius: 1,
                  cursor: 'zoom-in',
                  border: '1px solid #e0e0e0',
                  transition: 'transform 0.1s',
                  '&:hover': { transform: 'scale(1.02)' }
                }}
              />
            );
          })}
        </Stack>
      </TableCell>
    </TableRow>
  );
}

export default OnSiteChecklistStepRow;
